import { Controller } from "@hotwired/stimulus";
// import L from "leaflet";

export default class extends Controller {
  static targets = [
    "map",
    "latitude",
    "longitude",
    "coordinates",
    "locationAlert",
    "worldMap",
    "roundMap",
  ];

  static values = {
    actualLat: Number,
    actualLng: Number,
    guessLat: Number,
    guessLng: Number,
    spotName: String,
    roundIndex: Number,
    mapType: { type: String, default: "interactive" },
    bounds: Object,
    boundsFitted: { type: Boolean, default: false },
    customIconUrl: String,
  };

  connect() {
    // Initialize state
    this.iconReady = false;
    this.fallbackMode = false;

    // Check if Leaflet is already loaded
    console.log("map controller");
    if (typeof window.L === "undefined") {
      document.addEventListener(
        "leaflet:loaded",
        this.initializeMap.bind(this),
        { once: true }
      );
    } else {
      this.initializeMap();
    }

    // Listen for events
    document.addEventListener(
      "map:container-visible",
      this.onMapContainerVisible.bind(this)
    );
    document.addEventListener(
      "map:zoom-to-bounds",
      this.zoomToBounds.bind(this)
    );

    if (this.hasMapTarget) {
      this.mapTarget.addEventListener(
        "map:reinitialize",
        this.reinitializeMap.bind(this)
      );
    }

    // Preload custom icon if available
    this.preloadCustomIcon();
  }

  disconnect() {
    if (this.leafletTimeout) clearTimeout(this.leafletTimeout);
    window.removeEventListener("resize", this.handleResize.bind(this));
    document.removeEventListener(
      "map:container-visible",
      this.onMapContainerVisible.bind(this)
    );
    document.removeEventListener(
      "map:zoom-to-bounds",
      this.zoomToBounds.bind(this)
    );
    if (this.hasMapTarget) {
      this.mapTarget.removeEventListener(
        "map:reinitialize",
        this.reinitializeMap.bind(this)
      );
    }
    if (this.map) {
      this.map.remove();
    }
  }

  handleResize() {
    if (this.map) {
      this.map.invalidateSize();
    }
  }

  reinitializeMap() {
    console.log("Reinitializing map...");
    if (this.map) {
      this.map.invalidateSize(true);

      if (this.mapTypeValue === "spot") {
        const spotLat = parseFloat(this.actualLatValue);
        const spotLng = this.normalizeLng(this.actualLngValue);

        if (!isNaN(spotLat) && !isNaN(spotLng)) {
          this.map.setView([spotLat, spotLng], 14);
        }
      }
    }
  }

  /** Normalize longitude into [-180, 180] interval */
  normalizeLng(lng) {
    let lon = parseFloat(lng);
    if (lon < -180 || lon > 180) {
      lon = ((((lon + 180) % 360) + 360) % 360) - 180;
    }
    return lon;
  }

  preloadCustomIcon() {
    if (!this.hasCustomIconUrlValue) {
      this.iconReady = true;
      return;
    }

    const img = new Image();
    img.onload = () => {
      console.log("Custom icon loaded successfully");
      this.iconReady = true;
      this.fallbackMode = false;
    };
    img.onerror = () => {
      console.warn("Custom icon failed to load, using fallback");
      this.iconReady = true;
      this.fallbackMode = true;
    };
    img.src = this.customIconUrlValue;

    // Timeout fallback
    setTimeout(() => {
      if (!this.iconReady) {
        console.warn("Icon loading timeout, enabling fallback mode");
        this.iconReady = true;
        this.fallbackMode = true;
      }
    }, 3000);
  }

  createIcon(type = "guess") {
    try {
      if (this.hasCustomIconUrlValue && !this.fallbackMode) {
        const size = type === "spot" ? 20 : 24;
        const anchorPoint = size / 2;

        // Use divIcon with img tag for better SVG compatibility
        return window.L.divIcon({
          html: `<img src="${this.customIconUrlValue}" style="width: ${size}px; height: ${size}px; display: block;" />`,
          iconSize: [size, size],
          iconAnchor: [anchorPoint, anchorPoint],
          popupAnchor: [0, -anchorPoint],
          className: `custom-${type}-img`,
        });
      } else {
        // Clean fallback markers
        const color = type === "spot" ? "green" : "red";
        return window.L.icon({
          iconUrl: `https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-${color}.png`,
          shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
          iconSize: [25, 41],
          iconAnchor: [12, 41],
          popupAnchor: [1, -34],
          shadowSize: [41, 41],
        });
      }
    } catch (error) {
      console.error(`Error creating ${type} icon:`, error);
      // Ultimate fallback
      return window.L.icon({
        iconUrl:
          "https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img/marker-icon-2x-red.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });
    }
  }

  initializeMap() {
    try {
      console.log("initialize map");
      console.log(this.mapTypeValue);
      switch (this.mapTypeValue) {
        case "interactive":
          console.log("interactive it is");
          this.initializeInteractiveMap();
          break;
        case "round":
          console.log("round");
          this.initializeRoundMap();
          break;
        case "world":
          this.initializeWorldMap();
          break;
        case "spot":
          console.log("spot");
          this.initializeSpotMap();
          break;
        default:
          console.log("display");
          this.initializeDisplayMap();
      }

      window.addEventListener("resize", this.handleResize.bind(this));
    } catch (error) {
      console.error("Map init error:", error);
    }
  }

  initializeInteractiveMap() {
    const ensureContainerSize = () => {
      const container = this.mapTarget;
      if (container.offsetHeight < 200) {
        container.style.height = "50vh";
      }
    };

    ensureContainerSize();

    this.map = window.L.map(this.mapTarget, {
      worldCopyJump: false,
      minZoom: 2,
    }).setView([0, 0], 2);

    console.log("interactive map: ", this.map);

    // Satellite imagery
    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Tiles © Esri — Source: Esri, USGS, NOAA",
        noWrap: false,
        minZoom: 2,
        maxZoom: 18,
      }
    ).addTo(this.map);

    // Labels overlay
    window.L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "Labels © Esri",
        noWrap: false,
        minZoom: 2,
        maxZoom: 18,
      }
    ).addTo(this.map);

    this.map.on("click", this.handleMapClick.bind(this));
    this.setupVisibilityObserver();
    window.addEventListener(
      "resize",
      this.handleMapVisibilityChange.bind(this)
    );

    // Force a map size recalculation after initialization
    setTimeout(() => {
      this.map.invalidateSize(true);
    }, 100);
  }

  initializeDisplayMap() {
    this.map = window.L.map(this.mapTarget, {
      worldCopyJump: true,
      noWrap: true,
      center: [20, 0],
      zoom: 2,
      zoomControl: false,
      dragging: false,
      touchZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      scrollWheelZoom: false,
    });

    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles © Esri — Source: Esri, USGS, NOAA" }
    ).addTo(this.map);

    window.L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Labels © Esri" }
    ).addTo(this.map);

    if (this.hasLatitudeTarget && this.hasLongitudeTarget) {
      const lat = parseFloat(this.latitudeTarget.value);
      const lng = this.normalizeLng(this.longitudeTarget.value);
      if (!isNaN(lat) && !isNaN(lng)) {
        const ll = window.L.latLng(lat, lng, true);
        this.map.setView(ll, 10);
        window.L.marker(ll).addTo(this.map);
      }
    }
  }

  initializeRoundMap() {
    console.log("round map");
    if (this.mapTarget._leaflet_id) {
      this.mapTarget._leaflet_id = null;
    }
    const map = window.L.map(this.mapTarget, {
      worldCopyJump: true,
      noWrap: true,
    });

    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles © Esri" }
    ).addTo(map);

    window.L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Labels © Esri" }
    ).addTo(map);

    const actualLat = this.actualLatValue;
    const actualLng = this.normalizeLng(this.actualLngValue);
    const guessLat = this.guessLatValue;
    const guessLng = this.normalizeLng(this.guessLngValue);

    const circle = window.L.circle(
      window.L.latLng(actualLat, actualLng, true),
      {
        color: "#6366F1",
        fillColor: "#10B981",
        fillOpacity: 0.2,
        radius: 5000,
        weight: 3,
      }
    ).addTo(map);
    circle.bindPopup("Somewhere here 🤐");

    const guessLL = window.L.latLng(guessLat, guessLng, true);
    const guessIcon = this.createIcon("guess");
    const guessMarker = window.L.marker(guessLL, { icon: guessIcon }).addTo(
      map
    );
    guessMarker.bindPopup("Your guess");

    const distanceInMeters = map.distance(
      window.L.latLng(actualLat, actualLng, true),
      window.L.latLng(guessLat, guessLng, true)
    );
    const distanceInKm = (distanceInMeters / 1000).toFixed(1);

    if (distanceInMeters > 5000) {
      const angle = Math.atan2(guessLat - actualLat, guessLng - actualLng);
      const latMetersPerDegree = 111320;
      const lngMetersPerDegree = 111320 * Math.cos((actualLat * Math.PI) / 180);
      const latOffset = 5000 / latMetersPerDegree;
      const lngOffset = 5000 / lngMetersPerDegree;
      const circleEdgeY = actualLat + Math.sin(angle) * latOffset;
      const circleEdgeX = actualLng + Math.cos(angle) * lngOffset;
      const edgeLL = window.L.latLng(circleEdgeY, circleEdgeX, true);
      window.L.polyline([edgeLL, guessLL], {
        color: "#6366F1",
        weight: 2,
        opacity: 0.7,
      }).addTo(map);
      const midLat = (circleEdgeY + guessLat) / 2;
      const midLng = (circleEdgeX + guessLng) / 2;
      const angleDeg =
        (Math.atan2(guessLat - circleEdgeY, guessLng - circleEdgeX) * 180) /
        Math.PI;
      let adjustedAngle = angleDeg;
      if (adjustedAngle > 90 || adjustedAngle < -90)
        adjustedAngle = (adjustedAngle + 180) % 360;
      const distanceIcon = window.L.divIcon({
        className: "distance-label",
        html: `<div style="background-color:#4F46E5;color:white;border-radius:4px;padding:3px 8px;font-weight:bold;font-size:12px;box-shadow:0 1px 3px rgba(0,0,0,0.4);white-space:nowrap;transform:rotate(${adjustedAngle}deg);">${distanceInKm} km from the spot</div>`,
        iconSize: [150, 20],
        iconAnchor: [80, 10],
      });
      window.L.marker([midLat, midLng], {
        icon: distanceIcon,
        interactive: false,
      }).addTo(map);
    } else {
      guessMarker.bindPopup("Great guess! Within the area 👍").openPopup();
    }

    const bounds = window.L.latLngBounds([
      [actualLat - 0.1, actualLng - 0.1],
      [actualLat + 0.1, actualLng + 0.1],
      [guessLat, guessLng],
    ]);
    map.fitBounds(bounds, { padding: [20, 20] });
  }

  initializeWorldMap() {
    this.worldData = JSON.parse(this.element.dataset.rounds || "[]");
    this.map = window.L.map(this.mapTarget, {
      worldCopyJump: true,
      noWrap: true,
    }).setView([20, 0], 2);

    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Tiles © Esri — Source: Esri, USGS, NOAA" }
    ).addTo(this.map);
    window.L.tileLayer(
      "https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: "Labels © Esri" }
    ).addTo(this.map);

    const bounds = window.L.latLngBounds();
    const colors = ["6366F1", "EC4899", "14B8A6", "F97316", "8B5CF6"];

    this.worldData.forEach((round, i) => {
      const aLat = parseFloat(round.actualLat);
      const aLng = this.normalizeLng(round.actualLng);
      const gLat = parseFloat(round.guessLat);
      const gLng = this.normalizeLng(round.guessLng);

      const circle = window.L.circle(window.L.latLng(aLat, aLng, true), {
        color: `#${colors[i % colors.length]}`,
        fillColor: "#10B981",
        fillOpacity: 0.2,
        radius: 5000,
        weight: 3,
      }).addTo(this.map);
      circle.bindPopup(`Somewhere near ${round.spotName}<br>Round ${i + 1}`);

      const guessLL = window.L.latLng(gLat, gLng, true);
      const worldGuessIcon = this.createIcon("guess");

      window.L.marker(guessLL, { icon: worldGuessIcon })
        .addTo(this.map)
        .bindPopup(`Your guess<br>Round ${i + 1}`);

      const dist = this.map.distance(
        window.L.latLng(aLat, aLng, true),
        window.L.latLng(gLat, gLng, true)
      );
      if (dist > 5000) {
        const angle = Math.atan2(gLat - aLat, gLng - aLng);
        const latOffset = 5000 / 111320;
        const lngOffset = 5000 / (111320 * Math.cos((aLat * Math.PI) / 180));
        const edgeY = aLat + Math.sin(angle) * latOffset;
        const edgeX = aLng + Math.cos(angle) * lngOffset;
        const edgeLL = window.L.latLng(edgeY, edgeX, true);
        window.L.polyline([edgeLL, guessLL], {
          color: `#${colors[i % colors.length]}`,
          weight: 2,
          opacity: 0.7,
        }).addTo(this.map);
      }

      bounds.extend([aLat, aLng]);
      bounds.extend([gLat, gLng]);
    });

    console.log(
      "[WorldMap] bounds:",
      bounds,
      "isEmpty type:",
      typeof bounds.isEmpty
    );

    if (typeof bounds.isEmpty === "function" && !bounds.isEmpty()) {
      this.map.fitBounds(bounds, { padding: [20, 20] });
    }
  }

  initializeSpotMap() {
    console.log("Initializing spot map...");

    if (typeof window.L === "undefined") {
      console.error("Leaflet not available for spot map");
      return;
    }

    this.map = window.L.map(this.mapTarget, {
      worldCopyJump: false,
      noWrap: true,
      zoomControl: false,
      dragging: false,
      touchZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      scrollWheelZoom: false,
      keyboard: false,
      attributionControl: false,
    });

    window.L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      {
        attribution: "",
        noWrap: false,
        minZoom: 1,
        maxZoom: 18,
      }
    ).addTo(this.map);

    const spotLat = parseFloat(this.actualLatValue);
    const spotLng = this.normalizeLng(this.actualLngValue);

    console.log("Parsed coordinates:", spotLat, spotLng);

    if (!isNaN(spotLat) && !isNaN(spotLng)) {
      this.map.setView([spotLat, spotLng], 14);
      const spotIcon = this.createIcon("spot");
      window.L.marker([spotLat, spotLng], { icon: spotIcon }).addTo(this.map);
    } else {
      console.error("Invalid coordinates:", spotLat, spotLng);
      this.map.setView([0, 0], 2);
    }

    setTimeout(() => {
      this.map.invalidateSize(true);
    }, 100);
  }

  handleMapClick(e) {
    console.log("handle map click");
    if (this.marker) this.map.removeLayer(this.marker);

    // Place marker at exact click location for visual consistency
    const clickLat = e.latlng.lat;
    const clickLng = e.latlng.lng;

    const clickIcon = this.createIcon("guess");
    this.marker = window.L.marker([clickLat, clickLng], {
      icon: clickIcon,
    }).addTo(this.map);

    // But store normalized coordinates for backend consistency
    const normalizedLng = this.normalizeLng(clickLng);

    this.latitudeTarget.value = clickLat;
    this.longitudeTarget.value = normalizedLng;

    console.log(
      "Visual pin at:",
      clickLat,
      clickLng,
      "Stored as:",
      clickLat,
      normalizedLng
    );
    document.getElementById("location-alert")?.classList.add("hidden");
    document.dispatchEvent(
      new CustomEvent("map:pin-placed", {
        detail: {
          latitude: clickLat,
          longitude: normalizedLng,
          keepMapOpen: true,
        },
      })
    );
  }

  handleMapVisibilityChange() {
    if (
      this.map &&
      this.hasBoundsValue &&
      this.boundsValue &&
      !this.boundsFittedValue
    ) {
      this.map.invalidateSize();
      setTimeout(() => this.applyBounds(), 150);
    }
  }

  onMapContainerVisible() {
    if (
      this.map &&
      this.hasBoundsValue &&
      this.boundsValue &&
      !this.boundsFittedValue
    ) {
      this.map.invalidateSize();
      setTimeout(() => this.applyBounds(), 50);
    }
  }

  zoomToBounds(event) {
    if (!this.map) {
      console.error("Map not initialized");
      return;
    }

    const { bounds, source, countryName } = event.detail;
    if (!bounds) {
      console.error("No bounds provided in event");
      return;
    }

    console.log(
      "Zooming to bounds:",
      bounds,
      "Source:",
      source,
      "Country:",
      countryName
    );

    try {
      const mapOverlay = document.getElementById("map-overlay");
      if (mapOverlay && mapOverlay.classList.contains("hidden")) {
        console.log("Map overlay was hidden, triggering open event");
        document.dispatchEvent(new CustomEvent("map-toggle:open"));
        setTimeout(() => this.performZoom(bounds), 400);
        return;
      }

      this.performZoom(bounds);
    } catch (error) {
      console.error("Error zooming to bounds:", error);
    }
  }

  performZoom(bounds) {
    console.log("Performing zoom with bounds:", bounds);
    this.map.invalidateSize();

    let latLngBounds;
    if (bounds.south_west && bounds.north_east) {
      latLngBounds = window.L.latLngBounds(
        [bounds.south_west[0], bounds.south_west[1]],
        [bounds.north_east[0], bounds.north_east[1]]
      );
    } else if (bounds.southWest && bounds.northEast) {
      latLngBounds = window.L.latLngBounds(
        [bounds.southWest[0], bounds.southWest[1]],
        [bounds.northEast[0], bounds.northEast[1]]
      );
    } else {
      latLngBounds = window.L.latLngBounds(
        [bounds.south || -90, bounds.west || -180],
        [bounds.north || 90, bounds.east || 180]
      );
    }

    console.log("Created LatLngBounds:", latLngBounds);

    if (latLngBounds.isValid()) {
      console.log("Bounds are valid, flying to bounds");
      this.map.setView(latLngBounds.getCenter(), 4, { animate: false });

      setTimeout(() => {
        this.map.flyToBounds(latLngBounds, {
          padding: [50, 50],
          maxZoom: 6,
          duration: 1.5,
          easeLinearity: 0.25,
        });
      }, 100);
    } else {
      console.error("Invalid bounds:", latLngBounds);
    }
  }

  setupVisibilityObserver() {
    const mapContainer = this.mapTarget.closest(
      '[data-map-toggle-target="mapOverlay"]'
    );
    if (mapContainer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.attributeName === "class") {
            const isVisible = !mapContainer.classList.contains("hidden");
            if (isVisible) this.map.invalidateSize();
          }
        });
      });
      observer.observe(mapContainer, { attributes: true });
    } else {
      setTimeout(() => this.applyBounds(), 100);
    }
  }

  applyBounds() {
    if (!this.hasBoundsValue || !this.boundsValue || this.boundsFittedValue)
      return;
    try {
      let b;
      const val = this.boundsValue;
      if (val.southWest && val.northEast) b = [val.southWest, val.northEast];
      else if (val.south_west && val.north_east)
        b = [val.south_west, val.north_east];
      else
        b = [
          [val.south || 0, val.west || 0],
          [val.north || 0, val.east || 0],
        ];
      const latLngBounds = window.L.latLngBounds(b);
      if (latLngBounds.isValid()) {
        this.map.invalidateSize();
        this.map.setView(latLngBounds.getCenter(), 2, { animate: false });
        this.map.flyToBounds(latLngBounds, {
          padding: [50, 50],
          maxZoom: 7,
          duration: 0.75,
          easeLinearity: 0.3,
        });
        this.boundsFittedValue = true;
      } else {
        this.map.setView([0, 0], 2);
      }
    } catch {
      this.map.setView([0, 0], 2);
    }
  }
}
